import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus } from 'lucide-react';
import { meetingsApi } from '../services/api';
import { useAuthStore } from '../store/auth';
import MeetingCard from '../components/meeting/MeetingCard';
import CreateMeetingModal from '../components/meeting/CreateMeetingModal';
import clsx from 'clsx';

export default function CalendarPage() {
  const { user } = useAuthStore();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const weekStart = startOfWeek(currentWeek, { locale: ptBR });
  const weekEnd = endOfWeek(currentWeek, { locale: ptBR });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Fetch meetings for current week
  const { data: meetings = [], isLoading } = useQuery({
    queryKey: ['meetings', 'week', format(weekStart, 'yyyy-MM-dd')],
    queryFn: () => meetingsApi.getMeetingsByDateRange(weekStart, weekEnd),
  });

  // Filter for selected date
  const selectedDateMeetings = meetings
    .filter((m) => isSameDay(new Date(m.startTime), selectedDate))
    .filter((m) => user?.role === 'OPERATOR' ? m.userId === user.id : true)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const handlePreviousWeek = () => {
    setCurrentWeek(subWeeks(currentWeek, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentWeek(today);
    setSelectedDate(today);
  };

  const getMeetingCountForDay = (day: Date) => {
    return meetings.filter((m) => {
      const meetingDate = new Date(m.startTime);
      const matchesDate = isSameDay(meetingDate, day);
      const matchesUser = user?.role === 'OPERATOR' ? m.userId === user.id : true;
      return matchesDate && matchesUser;
    }).length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 safe-top">
        <div className="px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Agenda</h1>

          {/* Week Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePreviousWeek}
              className="p-2 text-gray-500 hover:text-gray-700 active:text-gray-900 tap-target"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="text-center">
              <p className="text-sm font-medium text-gray-900">
                {format(weekStart, 'MMM yyyy', { locale: ptBR })}
              </p>
              <p className="text-xs text-gray-500">
                {format(weekStart, 'd', { locale: ptBR })} -{' '}
                {format(weekEnd, 'd', { locale: ptBR })}
              </p>
            </div>

            <button
              onClick={handleNextWeek}
              className="p-2 text-gray-500 hover:text-gray-700 active:text-gray-900 tap-target"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Week Days */}
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day) => {
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              const meetingCount = getMeetingCountForDay(day);

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  className={clsx(
                    'flex flex-col items-center justify-center p-2 rounded-lg tap-target',
                    'transition-colors duration-150',
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : isToday
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <span className="text-xs font-medium uppercase mb-1">
                    {format(day, 'EEE', { locale: ptBR })}
                  </span>
                  <span className="text-lg font-semibold mb-1">
                    {format(day, 'd')}
                  </span>
                  {meetingCount > 0 && (
                    <span
                      className={clsx(
                        'text-xs px-1.5 py-0.5 rounded-full',
                        isSelected
                          ? 'bg-white text-primary-600'
                          : 'bg-primary-100 text-primary-700'
                      )}
                    >
                      {meetingCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Today Button */}
          <button
            onClick={handleToday}
            className="w-full mt-3 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 tap-target text-sm font-medium"
          >
            Ir para hoje
          </button>
        </div>
      </div>

      {/* Selected Date Meetings */}
      <div className="p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          {format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
        </h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : selectedDateMeetings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarIcon className="w-16 h-16 text-gray-300 mb-3" />
            <p className="text-gray-900 font-medium mb-1">Nenhum agendamento</p>
            <p className="text-gray-600 text-sm">Não há agendamentos para este dia</p>
          </div>
        ) : (
          <div className="space-y-3 pb-20">
            {selectedDateMeetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))}
          </div>
        )}
      </div>

      {/* FAB - Floating Action Button */}
      <button
        onClick={() => setIsCreateModalOpen(true)}
        className="fixed bottom-20 right-4 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 active:scale-95 transition-transform tap-target z-40"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Create Meeting Modal */}
      <CreateMeetingModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        defaultDate={selectedDate}
      />
    </div>
  );
}
